<template>
  <RevPitchBlock
    v-if="content.display"
    id="ecoBlocks"
    ref="root"
    :cards="cards"
    class="overflow-x-hidden"
    :subtitle="content.description"
    :title="content.title ?? ''"
  >
    <RevLink @click="handleModalOpen">
      {{ i18n(translations.modalLinkText) }}
    </RevLink>
    <EcoBlockModal />
  </RevPitchBlock>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'

import { productAPI } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevPitchBlock } from '@ds/components/PitchBlock'
import { useIntersectionObserver } from '@vueuse/core'

import { MODAL_NAMES } from '~/scopes/product/constants'

import { useProductTracking } from '../../composables/useProductTracking'

import translations from './EcoBlock.translations'
import EcoBlockModal from './components/EcoBlockModal/EcoBlockModal.vue'

const props = defineProps<{
  content: productAPI.GetEcoBlockResponse
  productTracking: ReturnType<typeof useProductTracking>
}>()

const root = ref<HTMLElement | null>()
const hasTriggeredImpression = ref(false)

const i18n = useI18n()
const { trackEcoBlockImpression } = useTracking()

const cards = computed(() =>
  (props.content.figures || []).map(({ title, description, type }) => ({
    title,
    description,
    image: {
      alt: '',
      height: 150,
      src: `/img/product/eco-block/${type}.svg`,
      width: 150,
    },
  })),
)

function handleModalOpen() {
  openModal(MODAL_NAMES.RENEWED_IMPACT)
}

const { stop } = useIntersectionObserver(root, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    trackEcoBlockImpression({
      pageType: 'product',
      ...props.productTracking.product.value,
    })
    hasTriggeredImpression.value = true
  }
})

watch(hasTriggeredImpression, () => {
  if (hasTriggeredImpression.value) {
    stop()
  }
})

onUnmounted(() => {
  stop()
})
</script>
